import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  parse,
  parseISO,
  set,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import { DayParam, SimpleDate } from './types';

export const getStartOfWeekDate = (simpleWeekDate: SimpleDate): SimpleDate => {
  const weekDate = simpleDateToDate(simpleWeekDate);
  return format(startOfWeek(weekDate, { weekStartsOn: 1 }), 'yyyy-MM-dd') as SimpleDate;
};

export const getStartAndEndOfMonth = (referenceDate: SimpleDate): [SimpleDate, SimpleDate] => {
  const date = simpleDateToDate(referenceDate);
  const startOfMonthDate = startOfMonth(date);
  const endOfMonthDate = endOfMonth(date);
  return [dateToSimpleDate(startOfMonthDate), dateToSimpleDate(endOfMonthDate)];
};

export const getStartAndEndOfWeek = (referenceDate: SimpleDate): [SimpleDate, SimpleDate] => {
  const date = simpleDateToDate(referenceDate);
  const weekStartDate = startOfWeek(date, { weekStartsOn: 1 });
  const weekEndDate = endOfWeek(date, { weekStartsOn: 1 });
  return [dateToSimpleDate(weekStartDate), dateToSimpleDate(weekEndDate)];
};

export const simpleDateToDate = (simpleDate: SimpleDate): Date => {
  return parse(simpleDate, 'yyyy-MM-dd', new Date());
};

export const dateToSimpleDate = (date: Date): SimpleDate => {
  return format(date, 'yyyy-MM-dd') as SimpleDate;
};

/**
 * A Date's calendar day in UTC as a SimpleDate. Unlike `dateToSimpleDate` (which
 * formats in the local timezone), this reads the UTC date — the correct choice for
 * puzzle dates, which are UTC-anchored, so it stays consistent between server (UTC)
 * and client (arbitrary timezone).
 */
export const dateToSimpleDateUTC = (date: Date): SimpleDate => {
  return date.toISOString().slice(0, 10) as SimpleDate;
};

export function dayArgToBoardDate(dayArg: DayParam, referenceDate: SimpleDate): SimpleDate | null {
  let localDate: Date | undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(dayArg)) {
    // dayArg is a string in YYYY-MM-DD format => that date's end of day (UTC)
    return dayArg as SimpleDate;
    // const [year, month, day] = dayArg.split('-').map(Number);
    // localDate = new Date(year, month - 1, day);
  } else {
    // dayArg is a number 1-7 => day of week (Monday=1, Sunday=7)
    const dayOfWeek = parseInt(dayArg, 10);
    if (Number.isInteger(dayOfWeek) && dayOfWeek >= 1 && dayOfWeek <= 7) {
      const dateAtEndOfDayUTC = set(parseISO(referenceDate), {
        hours: 23,
        minutes: 59,
        seconds: 59,
        milliseconds: 999,
      });

      const startOfUserWeek = startOfWeek(dateAtEndOfDayUTC, { weekStartsOn: 1 }); // Monday
      localDate = addDays(startOfUserWeek, dayOfWeek - 1);

      localDate = new Date(
        Date.UTC(
          localDate.getUTCFullYear(),
          localDate.getUTCMonth(),
          localDate.getUTCDate(),
          localDate.getUTCHours(),
          localDate.getUTCMinutes(),
          localDate.getUTCSeconds(),
          localDate.getUTCMilliseconds()
        )
      );
    } else {
      console.error('Invalid dayArg: ' + dayArg);
      return null;
    }
  }

  return format(localDate, 'yyyy-MM-dd') as SimpleDate;
}

export const getWeekStartAndEndUTC = (referenceDate: SimpleDate): [Date, Date] => {
  const referenceDateUTC = new UTCDate(referenceDate);
  const weekStartUTC = startOfWeek(referenceDateUTC, { weekStartsOn: 1 });
  const weekEndUTC = endOfWeek(referenceDateUTC, { weekStartsOn: 1 });
  return [weekStartUTC, weekEndUTC];
};

/** Monday (weekStartsOn: 1) of the week containing `date`. */
export function mondayOf(date: SimpleDate): SimpleDate {
  return dateToSimpleDate(startOfWeek(simpleDateToDate(date), { weekStartsOn: 1 }));
}

/** The date at (weekIndex weeks, weekday 0=Mon…6=Sun) after an epoch Monday. */
export function slotDate(epochMonday: SimpleDate, weekIndex: number, weekday: number): SimpleDate {
  return dateToSimpleDate(addDays(simpleDateToDate(epochMonday), weekIndex * 7 + weekday));
}

/** How many whole weeks `date`'s week is after the epoch Monday's week (can be negative). */
export function weekOffset(epochMonday: SimpleDate, date: SimpleDate): number {
  const ms = simpleDateToDate(mondayOf(date)).getTime() - simpleDateToDate(epochMonday).getTime();
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000));
}

/** The first day of the month containing `date`. */
export function startOfMonthSD(date: SimpleDate): SimpleDate {
  return dateToSimpleDate(startOfMonth(simpleDateToDate(date)));
}

/** The last day of the month containing `date`. */
export function endOfMonthSD(date: SimpleDate): SimpleDate {
  return dateToSimpleDate(endOfMonth(simpleDateToDate(date)));
}

/** The same day-of-month `n` months on, clamped to the month's length (n may be negative). */
export function addMonthsSD(date: SimpleDate, n: number): SimpleDate {
  return dateToSimpleDate(addMonths(simpleDateToDate(date), n));
}

/** The date `n` days after `date` (n may be negative). */
export function addDaysSD(date: SimpleDate, n: number): SimpleDate {
  return dateToSimpleDate(addDays(simpleDateToDate(date), n));
}

/** A month heading, e.g. "September 2026". */
export function monthLabel(date: SimpleDate): string {
  return format(simpleDateToDate(date), 'MMMM yyyy');
}

/** Whether two dates fall in the same calendar month (yyyy-MM prefix compare). */
export function sameMonth(a: SimpleDate, b: SimpleDate): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}
